import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Bell, Check, Clock, SlidersHorizontal, Sparkles, X } from 'lucide-react';
import { PRO_ACCESS_DAYS } from './SubscriptionFeatures';
import '../styles/pro-welcome.css';

const steps = [
  { target: 'navigation', icon: Sparkles, title: 'Your new favourite tab', label: 'Your Pro home', text: 'Find your premium features in Availability, right here in the main menu. Come back any time your next great meal is calling.' },
  { target: 'filters', icon: SlidersHorizontal, title: 'Make it your kind of evening', label: 'Find your table', text: 'Choose your party size and date, or search by restaurant name. Choose Not visited to discover places you have not marked as visited in your Tracker.' },
  { target: 'layouts', icon: SlidersHorizontal, title: 'Two ways to find your table', label: 'Choose your view', text: 'Switch between Time table for a week at a glance and Restaurants for photos and bookable times. Your search and filters apply to both views.' },
  { target: 'booking', icon: Clock, title: 'A week of possibilities', label: 'Read the time table', text: 'Dates run across the top and times down the side. Each number counts distinct restaurants, not tables or party sizes. Tap a number to see the restaurants, then choose Table for 2, 3 or 4 where available to book. Availability is confirmed on the booking page.' },
  { target: 'cards', icon: Clock, title: 'Browse somewhere delicious', label: 'Browse restaurants', text: 'The Restaurants view shows photos and times grouped by date and party size. On mobile, swipe sideways through restaurants and their times, or scroll down for more dates. Tap a time to open the restaurant’s booking page.' },
  { target: 'watch', icon: Bell, title: 'Let the table come to you', label: 'Your restaurant watchlist', text: 'Open Alert me to find restaurants with no tables listed. Choose up to 5 to watch; we’ll email when a scheduled check finds availability. Alerts cover every date and party size we check.' },
  { target: 'manage', icon: Check, title: 'You’re in. Make yourself at home.', label: 'You’re ready', text: 'Your alerts is where you can review your 5 restaurants, make room for new favourites, and send yourself a sample email. You can replay this tour beside your Pro pass any time.' },
];

interface Props { onFinish: () => void; preview?: boolean; onLayoutChange?: (layout: 'table' | 'restaurants') => void; }

const ProWelcomeTour: React.FC<Props> = ({ onFinish, preview = false, onLayoutChange }) => {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [missingBooking, setMissingBooking] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const next = useRef<HTMLButtonElement>(null);
  const finish = useRef(onFinish);
  finish.current = onFinish;
  const current = steps[step - 1];
  const goToStep = (value: number) => {
    onLayoutChange?.(steps[value - 1]?.target === 'cards' ? 'restaurants' : 'table');
    setStep(value);
  };

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const root = document.getElementById('root');
    const wasInert = root?.hasAttribute('inert');
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    root?.setAttribute('inert', '');
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') finish.current();
      if (event.key !== 'Tab') return;
      const buttons = panel.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
      if (!buttons?.length) return;
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      if (!wasInert) root?.removeAttribute('inert');
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', keydown);
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, []);

  useLayoutEffect(() => {
    next.current?.focus({ preventScroll: true });
    if (!current) { setRect(null); return; }
    let frame = 0;
    const locate = () => Array.from(document.querySelectorAll<HTMLElement>(`[data-pro-tour="${current.target}"]`)).find(el => el.getBoundingClientRect().width > 0);
    let target = locate();
    const needsAvailability = ['booking', 'layouts', 'cards'].includes(current.target);
    setMissingBooking(needsAvailability && !target);
    if (!target && needsAvailability) target = document.querySelector<HTMLElement>('[data-pro-tour="tables"]') || undefined;
    const measure = () => {
      if (!target) { setRect(null); return; }
      const box = target.getBoundingClientRect();
      setRect({ x: box.left - 7, y: box.top - 7, width: box.width + 14, height: box.height + 14 });
    };
    const position = () => {
      // Desktop and mobile have separate navigation links. Re-find the visible
      // one when the viewport changes while a step is open.
      target = locate() || (needsAvailability ? document.querySelector<HTMLElement>('[data-pro-tour="tables"]') || undefined : undefined);
      if (target) {
        const box = target.getBoundingClientRect();
        const freeHeight = window.innerHeight - (panel.current?.offsetHeight || 300) - 48;
        const top = Math.max(24, (freeHeight - box.height) / 2);
        window.scrollTo({ top: Math.max(0, window.scrollY + box.top - top), behavior: 'auto' });
      }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    position();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(position) : null;
    if (target) observer?.observe(target);
    if (panel.current) observer?.observe(panel.current);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', measure, true);
    };
  }, [current]);

  const Icon = current?.icon || Sparkles;
  return createPortal(
    <div className={`pro-tour ${step === 0 ? 'pro-tour-welcome' : ''}`}>
      <svg className="pro-tour-shade" aria-hidden="true" width="100%" height="100%">
        <defs><mask id="pro-tour-hole"><rect width="100%" height="100%" fill="white" />{rect && <rect className="pro-tour-spotlight-shape" {...rect} rx="16" fill="black" />}</mask></defs>
        <rect width="100%" height="100%" fill="#0c1a16" fillOpacity="0.73" mask="url(#pro-tour-hole)" />
        {rect && <g fill="none" stroke="#e6cd99">
          <rect className="pro-tour-spotlight-shape pro-tour-spotlight-halo" {...rect} rx="16" strokeWidth="6"
            style={{ '--halo-scale-x': 1 + 12 / rect.width, '--halo-scale-y': 1 + 12 / rect.height } as React.CSSProperties} />
          <rect className="pro-tour-spotlight-shape pro-tour-spotlight-edge" {...rect} rx="16" strokeWidth="1.5" />
        </g>}
      </svg>
      <div ref={panel} className={`pro-tour-panel ${step === 0 ? 'pro-tour-intro' : ''}`} role="dialog" aria-modal="true" aria-labelledby="pro-tour-title" aria-describedby="pro-tour-description">
        <button className="pro-tour-close" aria-label="Skip tour" onClick={onFinish}><X size={18} /></button>
        {step === 0 ? <>
          <div className="pro-welcome-seal"><Sparkles size={30} strokeWidth={1.5} /></div>
          <p className="pro-eyebrow">{preview ? 'Preview · Eatsy Pro' : 'Access unlocked · Eatsy Pro'}</p>
          <h1 id="pro-tour-title">Good tables.<br /><em>Great possibilities.</em></h1>
          <p id="pro-tour-description" className="pro-tour-description">Welcome to Pro. Your {PRO_ACCESS_DAYS}-day pass opens the door to your next memorable meal. Let’s show you around.</p>
          <div className="pro-tour-benefits">
            <span><Clock size={18} /><strong>Find a table</strong><small>A week at a glance</small></span>
            <span><Bell size={18} /><strong>Watch favourites</strong><small>Up to 5 restaurants</small></span>
            <span><ArrowRight size={18} /><strong>Book directly</strong><small>With the restaurant</small></span>
          </div>
          <button ref={next} className="pro-tour-next" onClick={() => goToStep(1)}>Show me around <ArrowRight size={17} /></button>
          <p className="pro-tour-footnote">Seven quick stops. Explore at your own pace.</p>
        </> : <>
          <div className="pro-tour-step-heading"><span key={current.target} className="pro-tour-icon"><Icon size={20} /></span><p className="pro-eyebrow">{current.label}</p><span className="pro-tour-counter">{step} / {steps.length}</span></div>
          <div aria-live="polite" aria-atomic="true">
            <div key={current.target} className="pro-tour-step-copy">
            <h2 id="pro-tour-title">{current.title}</h2>
            <p id="pro-tour-description" className="pro-tour-description">{missingBooking ? 'Available tables appear here, with times you can select to open the restaurant’s booking page. There are no matching times right now; try another filter or set up an alert.' : current.text}</p>
            </div>
          </div>
          <div className="pro-tour-progress" aria-label={`Step ${step} of ${steps.length}`}>{steps.map((item, index) => <span key={item.target} className={index < step ? 'is-complete' : ''} />)}</div>
          <div className="pro-tour-actions"><button className="pro-tour-back" onClick={() => goToStep(step - 1)}>Back</button><button ref={next} className="pro-tour-next" onClick={() => step === steps.length ? onFinish() : goToStep(step + 1)}>{step === steps.length ? 'Find my first table' : 'Next'} <ArrowRight size={17} /></button></div>
        </>}
      </div>
    </div>, document.body,
  );
};
export default ProWelcomeTour;
